import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Star, AlertCircle, CheckCircle, Download } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface FeedbackEntry {
  id: string;
  coach_id: string | null;
  user_email: string | null;
  feedback_type: 'bug' | 'improvement' | 'feature_request' | 'general';
  feature_area: string;
  rating: number | null;
  description: string;
  expected_behavior: string | null;
  actual_behavior: string | null;
  status: 'new' | 'reviewing' | 'planned' | 'resolved' | 'not_planned';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  admin_note: string | null;
  submitted_at: string;
  reviewed_at: string | null;
}

const TYPE_LABELS: Record<FeedbackEntry['feedback_type'], string> = {
  bug: 'Bug Report',
  improvement: 'Improvement',
  feature_request: 'Feature Request',
  general: 'General',
};

const AREA_LABELS: Record<string, string> = {
  practice_conversation: 'Practice Conversation',
  voice_practice: 'Voice Practice',
  feedback_analysis: 'Feedback & Analysis',
  knowledge_resources: 'Knowledge Resources',
  setup_access: 'Setup & Access',
  general: 'General',
};

const STATUS_LABELS: Record<FeedbackEntry['status'], string> = {
  new: 'New',
  reviewing: 'Reviewing',
  planned: 'Planned',
  resolved: 'Resolved',
  not_planned: 'Not Planned',
};

const PRIORITY_LABELS: Record<FeedbackEntry['priority'], string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

const STATUS_COLORS: Record<FeedbackEntry['status'], string> = {
  new: 'bg-orange-10 text-orange-70',
  reviewing: 'bg-blue-10 text-blue-70',
  planned: 'bg-teal-10 text-teal-50',
  resolved: 'bg-success-10 text-success-70',
  not_planned: 'bg-gray-10 text-gray-50',
};

const PRIORITY_COLORS: Record<FeedbackEntry['priority'], string> = {
  low: 'bg-gray-10 text-gray-50',
  normal: 'bg-blue-10 text-blue-60',
  high: 'bg-orange-10 text-orange-70',
  urgent: 'bg-red-10 text-red-70',
};

const TYPE_COLORS: Record<FeedbackEntry['feedback_type'], string> = {
  bug: 'bg-red-50 text-red-700',
  improvement: 'bg-teal-10 text-teal-50',
  feature_request: 'bg-brand-10 text-brand-60',
  general: 'bg-gray-10 text-gray-50',
};

export function NLPPracticeFeedbackReviews() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [selected, setSelected] = useState<FeedbackEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadFeedback();
  }, [user, navigate]);

  const loadFeedback = async () => {
    if (!user?.id) return;
    const isAdmin = await isUserAdmin(user.id);
    if (!isAdmin) {
      navigate('/login');
      return;
    }

    const { data, error: loadError } = await supabase
      .from('nlp_practice_feedback')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (loadError) {
      setError('We could not load the feedback right now.');
    } else {
      setFeedback((data || []) as FeedbackEntry[]);
    }
    setIsLoading(false);
  };

  const filtered = useMemo(() => feedback.filter((entry) => {
    const matchesSearch = `${entry.user_email || ''} ${entry.description}`
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || entry.status === statusFilter;
    const matchesType = typeFilter === 'all' || entry.feedback_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  }), [feedback, search, statusFilter, typeFilter]);

  const counts = {
    total: feedback.length,
    new: feedback.filter((f) => f.status === 'new').length,
    reviewing: feedback.filter((f) => f.status === 'reviewing').length,
    resolved: feedback.filter((f) => f.status === 'resolved').length,
  };

  const updateEntry = async (updates: Partial<FeedbackEntry>) => {
    if (!selected) return;
    setIsSaving(true);
    setError(null);

    const { data, error: updateError } = await supabase
      .from('nlp_practice_feedback')
      .update({ ...updates, reviewed_at: new Date().toISOString() })
      .eq('id', selected.id)
      .select()
      .maybeSingle();

    if (updateError || !data) {
      setError('We could not save those changes.');
    } else {
      const updated = data as FeedbackEntry;
      setFeedback((current) => current.map((f) => f.id === updated.id ? updated : f));
      setSelected(updated);
    }
    setIsSaving(false);
  };

  const downloadCSV = () => {
    const rows = [['Type', 'Area', 'Rating', 'Description', 'Expected', 'Actual', 'Status', 'Priority', 'Email', 'Submitted At']];
    filtered.forEach((f) => rows.push([
      TYPE_LABELS[f.feedback_type],
      AREA_LABELS[f.feature_area] || f.feature_area,
      f.rating ? `${f.rating}/5` : '',
      f.description.replace(/"/g, '""'),
      f.expected_behavior || '',
      f.actual_behavior || '',
      STATUS_LABELS[f.status],
      PRIORITY_LABELS[f.priority],
      f.user_email || '',
      new Date(f.submitted_at).toLocaleString(),
    ]));
    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'nlp-practice-feedback.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <LoadingSpinner message="Loading feedback..." />;

  return (
    <div className="min-h-screen bg-brand-5">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-2 hover:bg-gray-10 rounded-lg transition-colors text-gray-50">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="p-2.5 bg-sage-100 rounded-xl shadow-soft">
              <AlertCircle className="w-6 h-6 text-sage-600" />
            </div>
            <div>
              <h1 className="text-heading-xs text-gray-80">NLP Practice Feedback</h1>
              <p className="text-text-sm text-gray-40">Bug reports and suggestions from coaches</p>
            </div>
          </div>
          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-sage-600 hover:bg-sage-700 text-white text-sm font-medium rounded-xl transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total', value: counts.total, color: 'bg-brand-10 text-brand-60' },
            { label: 'New', value: counts.new, color: 'bg-orange-10 text-orange-50' },
            { label: 'Reviewing', value: counts.reviewing, color: 'bg-blue-10 text-blue-60' },
            { label: 'Resolved', value: counts.resolved, color: 'bg-success-10 text-success-70' },
          ].map((stat) => (
            <div key={stat.label} className="freud-card-elevated p-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}>
                {stat.label === 'Total' ? <AlertCircle className="w-5 h-5" /> : stat.label === 'New' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
              </div>
              <div className="text-heading-sm text-gray-80">{stat.value}</div>
              <div className="text-text-xs text-gray-40">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or description..."
              className="w-full pl-10 pr-4 py-3 border border-gray-20 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sage-400"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-3 border border-gray-20 rounded-xl text-sm bg-white text-gray-60">
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="planned">Planned</option>
            <option value="resolved">Resolved</option>
            <option value="not_planned">Not Planned</option>
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-4 py-3 border border-gray-20 rounded-xl text-sm bg-white text-gray-60">
            <option value="all">All types</option>
            <option value="bug">Bug Report</option>
            <option value="improvement">Improvement</option>
            <option value="feature_request">Feature Request</option>
            <option value="general">General</option>
          </select>
        </div>

        <div className="grid lg:grid-cols-[1fr_420px] gap-6 items-start">
          <div className="freud-card-elevated overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-16 text-center text-gray-40 text-sm">
                {search || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'No feedback matches your filters.'
                  : 'No feedback has been submitted yet.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-20">
                {filtered.map((entry, index) => (
                  <motion.button
                    key={entry.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    onClick={() => setSelected(entry)}
                    className={`w-full text-left p-5 hover:bg-gray-5 transition-colors ${selected?.id === entry.id ? 'bg-sage-5 border-l-4 border-sage-40' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${TYPE_COLORS[entry.feedback_type]}`}>
                          {TYPE_LABELS[entry.feedback_type]}
                        </span>
                        {entry.rating !== null && (
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <Star className="w-3.5 h-3.5" />
                            {entry.rating}
                          </span>
                        )}
                      </div>
                      <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[entry.status]}`}>
                        {STATUS_LABELS[entry.status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-40 mb-2">
                      <span>{AREA_LABELS[entry.feature_area] || entry.feature_area}</span>
                      <span>•</span>
                      <span>{entry.user_email || 'Anonymous'}</span>
                      <span>•</span>
                      <span>{new Date(entry.submitted_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-gray-60 line-clamp-2">{entry.description}</p>
                    {entry.priority !== 'normal' && (
                      <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_COLORS[entry.priority]}`}>
                        {PRIORITY_LABELS[entry.priority]}
                      </span>
                    )}
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          <div className="freud-card-elevated p-6 lg:sticky lg:top-6">
            {!selected ? (
              <div className="py-16 text-center text-gray-40">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 text-sage-40" />
                <p className="text-sm">Select a feedback entry to read and review it.</p>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold mb-2 ${TYPE_COLORS[selected.feedback_type]}`}>
                      {TYPE_LABELS[selected.feedback_type]}
                    </span>
                    <h2 className="text-text-xl text-gray-80">
                      {AREA_LABELS[selected.feature_area] || selected.feature_area}
                    </h2>
                  </div>
                  {selected.rating !== null && (
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className={`w-4 h-4 ${star <= selected.rating! ? 'text-amber-500' : 'text-gray-20'}`} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-xs text-gray-40 mb-4">
                  {selected.user_email || 'Anonymous'} • {new Date(selected.submitted_at).toLocaleString()}
                </div>

                <div className="px-4 py-4 rounded-xl bg-sage-5 border border-sage-10 mb-5">
                  <p className="text-gray-70 leading-relaxed whitespace-pre-wrap">{selected.description}</p>
                </div>

                {selected.expected_behavior && (
                  <div className="mb-4">
                    <div className="text-xs font-bold uppercase tracking-wide text-gray-40 mb-1">Expected</div>
                    <p className="text-sm text-gray-60">{selected.expected_behavior}</p>
                  </div>
                )}
                {selected.actual_behavior && (
                  <div className="mb-4">
                    <div className="text-xs font-bold uppercase tracking-wide text-gray-40 mb-1">Actual</div>
                    <p className="text-sm text-gray-60">{selected.actual_behavior}</p>
                  </div>
                )}

                <div className="border-t border-gray-20 pt-5 space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-70 mb-2">Status</label>
                    <select
                      value={selected.status}
                      onChange={(e) => updateEntry({ status: e.target.value as FeedbackEntry['status'] })}
                      disabled={isSaving}
                      className="w-full px-3 py-2.5 border border-gray-20 rounded-xl text-sm bg-white"
                    >
                      <option value="new">New</option>
                      <option value="reviewing">Reviewing</option>
                      <option value="planned">Planned</option>
                      <option value="resolved">Resolved</option>
                      <option value="not_planned">Not Planned</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-70 mb-2">Priority</label>
                    <select
                      value={selected.priority}
                      onChange={(e) => updateEntry({ priority: e.target.value as FeedbackEntry['priority'] })}
                      disabled={isSaving}
                      className="w-full px-3 py-2.5 border border-gray-20 rounded-xl text-sm bg-white"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-70 mb-2">Private admin note</label>
                    <textarea
                      defaultValue={selected.admin_note || ''}
                      key={selected.id + (selected.admin_note || '')}
                      onBlur={(e) => {
                        const note = e.target.value.trim() || null;
                        if (note !== selected.admin_note) updateEntry({ admin_note: note });
                      }}
                      disabled={isSaving}
                      rows={4}
                      placeholder="Add a private note about this feedback..."
                      className="w-full px-3 py-2.5 border border-gray-20 rounded-xl text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-sage-400"
                    />
                  </div>

                  {isSaving && <p className="text-xs text-gray-40">Saving...</p>}
                </div>

                <div className="mt-5 pt-4 border-t border-gray-20 text-xs text-gray-40">
                  Submitted {new Date(selected.submitted_at).toLocaleString()}
                  {selected.reviewed_at && ` • Last reviewed ${new Date(selected.reviewed_at).toLocaleString()}`}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
